import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { AlertTriangle, Check, X, Download, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

type ProcessingResult = {
  status: 'success' | 'error' | 'pending';
  passport: string;
  operatorName: string;
  pilgrimName?: string;
  currentOperator?: string;
  message?: string;
};

const BulkOperatorTransferStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    error: 0,
    pending: 0,
    currentBatch: 0
  });

  useEffect(() => {
    const processData = async () => {
      try {
        if (!location.state || !location.state.fileData) {
          toast.error('No file data found. Please upload again.');
          navigate('/my-operation-list');
          return;
        }

        const fileData = location.state.fileData;
        setIsProcessing(true);
        
        // Parse Excel data
        const workbook = XLSX.read(fileData, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        
        setStats(prev => ({ ...prev, total: rows.length }));
        
        // Process in smaller batches to avoid UI freezing
        const BATCH_SIZE = 5;
        const results: ProcessingResult[] = [];
        
        // Get current user info
        const userId = localStorage.getItem('userId');
        const userRole = localStorage.getItem('userRole');
        
        if (!userId || !userRole) {
          toast.error('Authentication error. Please log in again.');
          navigate('/login');
          return;
        }
        
        // Process each row
        for (let i = 0; i < rows.length; i += BATCH_SIZE) {
          const batch = rows.slice(i, i + BATCH_SIZE);
          
          // Update batch status
          setStats(prev => ({ 
            ...prev, 
            currentBatch: Math.min(i + BATCH_SIZE, rows.length),
            pending: rows.length - Math.min(i + BATCH_SIZE, rows.length)
          }));
          
          const batchPromises = batch.map(async (row: any, index: number) => {
            try {
              // Validate row data
              if (!row.passport_number || !row.new_operator_name) {
                return {
                  status: 'error' as const,
                  passport: row.passport_number || 'Unknown',
                  operatorName: row.new_operator_name || 'Unknown',
                  message: 'Missing passport or operator name'
                };
              }
              
              // Step 1: Find pilgrim by passport
              const operationRef = collection(firestore, 'operationlist');
              const operationQuery = query(
                operationRef, 
                where('passportNumber', '==', row.passport_number)
              );
              
              const operationSnapshot = await getDocs(operationQuery);
              
              if (operationSnapshot.empty) {
                return {
                  status: 'error' as const,
                  passport: row.passport_number,
                  operatorName: row.new_operator_name,
                  message: 'Pilgrim not found in operation list'
                };
              }
              
              // Get the operation entry document
              const opEntry = operationSnapshot.docs[0];
              const opData = opEntry.data();
              
              // Step 2: Find guide by name
              const guidesRef = collection(firestore, 'Guides');
              const guidesQuery = query(
                guidesRef,
                where('name', '==', row.new_operator_name)
              );
              
              const guidesSnapshot = await getDocs(guidesQuery);
              
              if (guidesSnapshot.empty) {
                return {
                  status: 'error' as const,
                  passport: row.passport_number,
                  operatorName: row.new_operator_name,
                  pilgrimName: opData.pilgrimName,
                  currentOperator: opData.operatorName,
                  message: 'New operator not found'
                };
              }

              // Get guide data
              const guideDoc = guidesSnapshot.docs[0];
              const guideData = guideDoc.data();
              
              // Step 3: Check if user is authorized - ONLY checking if user is the operator's organizer
              if (userRole === 'organizer') {
                // ONLY check if user is the organizer of the current operator
                let isAuthorized = false;
                
                if (opData.operatorId) {
                  const currentOperatorRef = doc(firestore, 'Guides', opData.operatorId);
                  const currentOperatorDoc = await getDoc(currentOperatorRef);
                  
                  if (currentOperatorDoc.exists()) {
                    const currentOperatorData = currentOperatorDoc.data();
                    if (currentOperatorData.organizerId === userId) {
                      isAuthorized = true;
                    }
                  }
                }
                
                // Check authorization
                if (!isAuthorized) {
                  return {
                    status: 'error' as const,
                    passport: row.passport_number,
                    operatorName: row.new_operator_name,
                    pilgrimName: opData.pilgrimName,
                    currentOperator: opData.operatorName,
                    message: 'Not authorized to modify this pilgrim'
                  };
                }
              }
              
              // Step 4: Check if the new operator belongs to the current user's organization
              if (userRole === 'organizer' && guideData.organizerId !== userId) {
                return {
                  status: 'error' as const,
                  passport: row.passport_number,
                  operatorName: row.new_operator_name,
                  pilgrimName: opData.pilgrimName,
                  currentOperator: opData.operatorName,
                  message: 'New operator must belong to your organization'
                };
              }
              
              // Step 5: Get organizer data for new guide
              const organizerRef = doc(firestore, 'Organizers', guideData.organizerId);
              const organizerDoc = await getDoc(organizerRef);
              
              if (!organizerDoc.exists()) {
                return {
                  status: 'error' as const,
                  passport: row.passport_number,
                  operatorName: row.new_operator_name,
                  pilgrimName: opData.pilgrimName,
                  currentOperator: opData.operatorName,
                  message: 'Organizer data not found'
                };
              }
              
              const organizerData = organizerDoc.data();
              
              // Step 6: Update the operation entry
              await writeBatch(firestore)
                .update(opEntry.ref, {
                  operatorId: guideDoc.id,
                  operatorName: guideData.name,
                  operatorOrganizerId: organizerData.id,
                  operatorOrganizerName: organizerData.name,
                  updatedAt: Date.now()
                })
                .commit();
              
              // Return success
              return {
                status: 'success' as const,
                passport: row.passport_number,
                operatorName: row.new_operator_name,
                pilgrimName: opData.pilgrimName,
                currentOperator: opData.operatorName,
                message: 'Operator transferred successfully'
              };
              
            } catch (error: any) {
              console.error('Error processing row:', error);
              return {
                status: 'error' as const,
                passport: row.passport_number || 'Unknown',
                operatorName: row.new_operator_name || 'Unknown',
                message: error.message || 'Unknown error'
              };
            }
          });
          
          const batchResults = await Promise.all(batchPromises);
          
          // Update stats for this batch
          const batchStats = batchResults.reduce(
            (acc, result) => {
              acc[result.status]++;
              return acc;
            },
            { success: 0, error: 0, pending: 0 }
          );
          
          setStats(prev => ({ 
            ...prev,
            success: prev.success + batchStats.success,
            error: prev.error + batchStats.error
          }));
          
          results.push(...batchResults);
          
          // Update results immediately after each batch
          setResults([...results]);
          
          // Small delay to allow UI to update
          await new Promise(resolve => setTimeout(resolve, 10));
        }
        
        // Final update
        setResults(results);
        setIsProcessing(false);
        
        // Export results
        exportResults(results);
        
      } catch (error: any) {
        console.error('Error processing file:', error);
        toast.error(error.message || 'Error processing file');
        setIsProcessing(false);
      }
    };

    processData();
  }, [location.state, navigate]);

  const exportResults = (results: ProcessingResult[]) => {
    try {
      // Create worksheet
      const exportData = results.map(result => ({
        passport_number: result.passport,
        new_operator_name: result.operatorName,
        pilgrim_name: result.pilgrimName || '',
        current_operator: result.currentOperator || '',
        status: result.status,
        message: result.message || ''
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add Arabic headers
      XLSX.utils.sheet_add_aoa(ws, [[
        'رقم الجواز',
        'المرشد الجديد',
        'اسم الحاج', 
        'المرشد الحالي',
        'الحالة',
        'الملاحظات'
      ]], { origin: 'A1' });
      
      // Set column widths
      ws['!cols'] = [
        { wch: 15 }, // passport
        { wch: 20 }, // new operator
        { wch: 30 }, // pilgrim name
        { wch: 20 }, // current operator
        { wch: 10 }, // status
        { wch: 40 }, // message
      ];
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
      
      // Save to file
      const fileName = `bulk-operator-transfer-results-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting results:', error);
      toast.error('Error exporting results');
    }
  };

  const goBack = () => {
    navigate('/my-operation-list');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Bulk Operator Transfer Results</h1>
            <p className="text-gray-500 mt-1">Results of your bulk operator transfer operation</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goBack}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={18} />
              <span>Back to Operation List</span>
            </button>
            <button
              onClick={() => exportResults(results)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={18} />
              <span>Export Results</span>
            </button>
          </div>
        </div>
      </div>

      {/* Progress */}
      {isProcessing && (
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <Loader2 className="animate-spin text-primary-500" size={24} />
            <h2 className="text-lg font-semibold">Processing...</h2>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Processing rows: {stats.currentBatch} of {stats.total}</span>
              <span>{Math.round((stats.currentBatch / stats.total) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div 
                className="bg-primary-500 h-full transition-all"
                style={{ width: `${(stats.currentBatch / stats.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-lg font-semibold mb-1">Total</div>
          <div className="text-3xl font-bold text-primary-600">{stats.total}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-lg font-semibold mb-1">Successful</div>
          <div className="text-3xl font-bold text-green-600">{stats.success}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-lg font-semibold mb-1">Failed</div>
          <div className="text-3xl font-bold text-red-600">{stats.error}</div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h2 className="text-xl font-bold mb-4">Transfer Results</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Passport
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pilgrim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Operator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  New Operator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.map((result, index) => (
                <tr key={index} className={result.status === 'success' ? 'bg-green-50' : result.status === 'error' ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {result.passport}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.pilgrimName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.currentOperator || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.operatorName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      result.status === 'success' 
                        ? 'bg-green-100 text-green-800'
                        : result.status === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {result.status === 'success' ? (
                        <><Check size={12} className="mr-1" /> Successful</>
                      ) : result.status === 'error' ? (
                        <><X size={12} className="mr-1" /> Failed</>
                      ) : (
                        <><AlertTriangle size={12} className="mr-1" /> Pending</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                    {result.message || '-'}
                  </td>
                </tr>
              ))}
              
              {results.length === 0 && !isProcessing && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    No results found. Please try again.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BulkOperatorTransferStatusPage;

export { BulkOperatorTransferStatusPage };